// @ts-strict-ignore
import { AfterViewInit, Component, EventEmitter, Input, OnChanges, OnInit, Output, Signal, SimpleChanges, viewChild } from '@angular/core';
import { LocalizedPropertyService } from '@shared/localization/localized-property.service';
import { ITableProvider, TableProviderType } from '../table-provider';
import Handsontable from 'handsontable';
import { CellCoords } from '../handsontable-3rdparty/coords';
import { isConnectionSpanData, ISpanData } from '../span-data';
import { LineSpanInfo } from '../line-span-info';
import { LabelEditorUtils } from '../label-editor-utils';
import { QuillTableRenderer } from '../quill-table-renderer';
import { LocaleResourceManager } from '@shared/manager/locale-resource.manager';
import { NumberUtils } from '@shared/util/number-utils';
import { TableRenderingState } from '../label-editor.component';
import { ArticleVO } from '@article/model/vo/article.vo';
import { LocaleUtils } from '@shared/util/locale-utils';
import { ArrayUtils } from '@shared/util/array-utils';
import { CellRange } from '../handsontable-3rdparty/range';

type MergeParent = {
  row: number;
  col: number;
  rowspan: number;
  colspan: number;
}

@Component({
  standalone: false,
  selector: 'label-editor-tab',
  templateUrl: 'label-editor-tab.component.html',
  styleUrls: ['./label-editor-tab.component.scss'],
})
export class LabelEditorTabComponent implements OnInit, AfterViewInit, OnChanges {
  private static SELECTED_CELLS_DESCRIPTION_SEPARATOR: string = ',  ';
  public static SELECTED_CELLS_DESCRIPTION_POSITION_SEPARATOR: string = ') ';

  private descriptionRef:Signal<any> = viewChild('description');

  @Input() public tableProvider: ITableProvider = null;
  @Input() public targetState: string; // basicControl or advancedControl
  @Input() public markingSystemLabelModeEnabled: boolean;
  @Input() public quillTableEditor: any;
  @Input() public tableRenderingState: TableRenderingState;
  @Input() public height: number;
  @Input() public isSelected: boolean;
  @Input() public isSelectedCellsDescriptionEnabled: boolean;

  // table event handlers associated with toolbar and editor
  @Input() public afterTableChangeHandler: Function;
  @Input() public afterTableScrollHorizontallyHandler: Function;
  @Input() public afterTableScrollVerticallyHandler: Function;
  @Input() public beforeTableOnCellMouseDownHandler: Function;
  @Input() public afterTableOnCellMouseOverHandler: Function;
  @Input() public afterTableOnCellMouseOutHandler: Function;
  @Input() public afterTablePasteHandler: Function;
  @Input() public afterTableBeginEditingHandler: Function;
  @Input() public shouldDeselectCellsOnOutsideClickHandler: Function;

  // used in undo/redo change-multiline and merge-and-divide actions
  @Input() public changeMultilineCount: Function;
  @Input() public mergeAndDivideCells: Function;

  @Output()
  public tableCreatedEmitter: EventEmitter<any> = new EventEmitter();
  @Output()
  public afterTableSelectionEndEmitter: EventEmitter<void> = new EventEmitter();

  public isTableProviderEmpty: boolean = false;
  public isDirectPrintingTab: boolean = false;
  public selectedCellsDescriptionMaterial: string = '';
  public selectedCellsDescriptionPosition: string = '';

  // internal table provider data build by initTableProvider()
  private rowHeaders: string[];
  private columnHeaders: string[];
  private columnWidths: number[];
  private rowHeights: number[];
  private initialMergeCells: Object[];

  // table component: variable is untyped because ts-definition from module file is missing existing functions in regard to undo/redo
  private table: any;

  // table rendering
  public labelEditorInitialized: boolean = false;
  private originalTableRender: Function;
  private tableRendererHandler = (hot, td, r, c, prop, v, cProps) => QuillTableRenderer.render(this, hot, td, r, c, prop, v, cProps);
  private tableRenderHandler = () => this.renderTable();
  private afterViewInitHandler = () => this.afterViewInit();

  // table event handlers
  private beforeTableSetRangeEndHandler = (coords: CellCoords) => this.beforeTableSetRangeEnd(coords);
  private beforeTableMergeCellsHandler = (cellRange: CellRange) => this.beforeTableMergeCells(cellRange);
  private beforeTableDivideCellsHandler = (cellRange: CellRange) => this.beforeTableDivideCells(cellRange);
  private beforeTableCopyHandler = (data: any[], coords: any[]) => this.beforeTableCopy(data, coords);
  private beforeTablePasteHandler = (data: any[], coords: any[]) => this.beforeTablePaste(data, coords);
  private tableCellsUpdateHandler = (row?: number, col?: number) => this.updateTableCells(row, col);
  private afterTableDeselectHandler = () => this.afterTableDeselect();
  private afterTableSelectionEndHandler = () => this.afterTableSelectionEnd();

  constructor(private localeResourceManager: LocaleResourceManager, private readonly localizedPropertyService: LocalizedPropertyService) {}

  public ngOnInit(): void {
    this.isDirectPrintingTab = this.tableProvider.type === TableProviderType.DIRECT_PRINTING;
    this.initTableProvider();
  }

  public ngAfterViewInit(): void {
    // waiting for quillTableEditor to be provided by the parent component
    setTimeout(this.afterViewInitHandler, 0);
  }

  public afterViewInit(): void {
    const columns = [];
    for (let i = 0; i < this.columnWidths.length; i++) {
      columns.push({ data: i + '.html5data', editor: this.quillTableEditor, renderer: this.tableRendererHandler, width: this.columnWidths[i] });
    }

    // create instance with no main settings yet set, to get custom render overwrites in
    // auto sizing of ros and columns need to be set here, so handsontable does not use sampleGenerator,
    // which would trigger calls to getting not needed (not visible) data from spanData:
    this.table = new Handsontable(document.getElementById('labelEditorTable_' + this.tableProvider.id), {
      autoColumnSize: false,
      autoRowSize: false,
    });
    this.originalTableRender = this.table.view.render;
    this.table.view.render = this.tableRenderHandler;
    this.table.updateSettings({
      columns: columns,
      data: this.tableProvider.table,
      rowHeaders: this.rowHeaders, // row headers with no content and no width are used to give height for not yet used multilines
      rowHeaderWidth: 0,
      colHeaders: this.columnHeaders,
      rowHeights: this.rowHeights,
      filters: true,
      dropdownMenu: false,
      contextMenu: false,
      height: this.getTableHeight(),
      afterChange: this.afterTableChangeHandler,
      beforeSetRangeEnd: this.beforeTableSetRangeEndHandler,
      afterSelectionEnd: this.afterTableSelectionEndHandler,
      afterScrollHorizontally: this.afterTableScrollHorizontallyHandler,
      afterScrollVertically: this.afterTableScrollVerticallyHandler,
      beforeOnCellMouseDown: this.beforeTableOnCellMouseDownHandler,
      afterOnCellMouseOver: this.afterTableOnCellMouseOverHandler,
      afterOnCellMouseOut: this.afterTableOnCellMouseOutHandler,
      afterDeselect: this.afterTableDeselectHandler,
      outsideClickDeselects: this.shouldDeselectCellsOnOutsideClickHandler,
      beforeGetCellMeta: function (r, c, props) {
        if (props.readOnly) {
          props.disableVisualSelection = false;
        }
        // somehow handsontable has a problem with keeping correct track of spanned and thus copyable property
        // thus, handsontable incorrectly does not allow copying of content in (no longer spanned) multilines
        // to allow this, we simply set copyable property here for true.
        // correct dealings with copy & paste is then done in custom handlers
        props.copyable = true;
      },
      beforePaste: this.beforeTablePasteHandler,
      afterPaste: this.afterTablePasteHandler,
      beforeCopy: this.beforeTableCopyHandler,
      mergeCells: this.initialMergeCells,
      cells: this.tableCellsUpdateHandler,
      // renderAllRows: true, // rendering all rows disables virtual scrolling
      // setting viewportColumnRenderingOffset to columns.length disables virtual scrolling for cols (see line above for rows)
      viewportColumnRenderingOffset: 4, // value must not be too low, otherwise it disruppts scrolling with pressing and holding arrow keys
      viewportRowRenderingOffset: 4,
      autoWrapCol: false,
      autoWrapRow: false,
    });
    this.table.addHook('beforeMergeCells', this.beforeTableMergeCellsHandler);
    this.table.addHook('beforeUnmergeCells', this.beforeTableDivideCellsHandler);
    this.table.addHook('afterBeginEditing', this.afterTableBeginEditingHandler);
    this.table.labelEditorTabComponent = this; // used in ../handsontable-undoredo/change-multiline-action.ts
    this.tableProvider.activate();
    this.labelEditorInitialized = true;
    this.table.render();
    this.table.clearUndo();
    this.tableCreatedEmitter.emit({ identity: this.tableProvider.id, table: this.table });
  }

  private initTableProvider(): void {
    this.columnHeaders = [];
    this.rowHeaders = [];
    this.columnWidths = [];
    this.rowHeights = [];
    this.initialMergeCells = [];
    if (this.tableProvider === null || this.tableProvider.table === null) {
      this.isTableProviderEmpty = true;
      return;
    }
    this.tableProvider.deactivate();
    const maxColumnCount: number = this.tableProvider.colCount;
    for (let i = 0; i < this.tableProvider.table.length; i++) {
      const row: ISpanData[] = this.tableProvider.table[i];
      if (row) {
        const lineSpanInfo: LineSpanInfo = this.tableProvider.getLineSpanInfoForRow(i);
        let rowHeight: number = 0;
        for (let j = 0; j < row.length; j++) {
          const spanData: ISpanData = row[j];
          const mainSpanData: ISpanData = lineSpanInfo === null ? spanData : this.tableProvider.getSpanData(lineSpanInfo.startRow, j);
          if (spanData) {
            spanData['useStrictTextCheckConformity'] = this.useStrictTextCheckConformity();
            if (mainSpanData.parent === null) {
              rowHeight = Math.max(rowHeight, mainSpanData.labelFormat.height);
            }
            if (this.columnWidths[j]) {
              this.columnWidths[j] = Math.max(0, this.columnWidths[j], spanData.labelFormat.width);
            } else {
              this.columnWidths[j] = Math.max(0, spanData.labelFormat.width);
            }
            if (spanData.rowSpan > 1 || spanData.colSpan > 1) {
              this.initialMergeCells.push({ row: i, col: j, rowspan: spanData.rowSpan, colspan: spanData.colSpan });
            }
          }
        }
        // in case row with multilined labels is shorter then maximum column count,
        // we need to merge multilines so that cells with no spanData are rendered correctly:
        if (lineSpanInfo && lineSpanInfo.lineSpan > 1 && lineSpanInfo.startRow === i) {
          for (let j = row.length; j < maxColumnCount; j++) {
            this.initialMergeCells.push({ row: i, col: j, rowspan: lineSpanInfo.lineSpan, colspan: 1 });
          }
        }

        // calculate row height:
        if (lineSpanInfo !== null && lineSpanInfo.lineSpan > 1) {
          // we need to make sure to have whole and equal numbers for row heights,
          // otherwise for example rounding applied by browser in rendering causes incorrect cell sizes when multilines are used
          const rowHeightFraction: number = rowHeight / lineSpanInfo.lineSpan;
          rowHeight = Math.round(rowHeightFraction);
        }
        rowHeight = Math.max(7, rowHeight); // content row height has minimum of 7, otherwise it can cause display issues
        // We need to add 1 to rowHeight here, because handsontable will reduce height by 1 later, to consider bottom border of cell
        // but such border does not exist for row headers, where such height information will be added by handsontable.
        // We addittionally need to add 1 for border which will be present in td-elements:
        this.rowHeights.push(rowHeight + 2);
        this.rowHeaders.push(''); // for each row, we add header with no content, see comment where handsontable is created
      }
    }
    for (let i = 0; i < maxColumnCount; i++) {
      const columnHeader: string = this.getColumnHeader(i);
      const columnHeaderTooltip: string = this.tableProvider.getColumnHeaderTooltip(i);
      if (columnHeaderTooltip) {
        this.columnHeaders.push(
          '<span class="colHeader colHeaderText" data-le-column-header-tooltip-text="' + columnHeaderTooltip + '">' + columnHeader + '</span>'
        );
      } else {
        this.columnHeaders.push(columnHeader);
      }
    }
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (this.table && (changes.height || changes.isSelected)) {
      setTimeout(() => this.table.updateSettings({ height: this.getTableHeight() }, true));
    }
  }

  private getTableHeight(): number {
    return this.height - (this.descriptionRef ? this.descriptionRef().nativeElement.clientHeight : 0);
  }

  private getColumnHeader(columnIndex: number): string {
    switch (this.tableProvider.type) {
      case TableProviderType.MARKING_CARD:
      case TableProviderType.DIRECT_PRINTING:
        return columnIndex + 1 + '';
      case TableProviderType.SEPARATE_LABELING:
        return '';
      default:
        return this.tableProvider.getColumnHeader(columnIndex);
    }
  }

  private renderTable(): void {
    if (this.tableRenderingState.isEnabled && this.labelEditorInitialized) {
      this.originalTableRender.apply(this.table.view);
    }
  }

  public useStrictTextCheckConformity(): boolean {
    return this.targetState === 'advancedControl';
  }

  // handsontable has bug in dealing with selection highlight in case all cells (with merges) are selected in a row or column:
  private beforeTableSetRangeEnd(coords: CellCoords) {
    const selRange = this.table.getSelectedRangeLast();
    selRange.highlight = new CellCoords(selRange.highlight.row, selRange.highlight.col); // clone in case we will modify its reference
    selRange.to = coords;
    // the following if-statement must be excatly so, as it is taken from handsontable. Handsontable incorrectly skips expanding range for merged cells in such cases:
    if (
      (selRange.from.row === 0 && selRange.to.row === this.table.countRows() - 1) ||
      (selRange.from.col === 0 && selRange.to.col === this.table.countCols() - 1)
    ) {
      const mergeCellsPlugin: any = this.table.getPlugin('mergeCells');
      let rangeExpanded: boolean = false;
      do {
        rangeExpanded = false;
        for (let i: number = 0; i < mergeCellsPlugin.mergedCellsCollection.mergedCells.length; i++) {
          const cellInfo = mergeCellsPlugin.mergedCellsCollection.mergedCells[i];
          const mergedCellRange = cellInfo.getRange();
          if (selRange.expandByRange(mergedCellRange)) {
            coords.row = selRange.to.row;
            coords.col = selRange.to.col;
            rangeExpanded = true;
          }
        }
      } while (rangeExpanded);
    }
  }

  private determineMergeParent(cellRange: CellRange): MergeParent {
    const topLeft = cellRange.getTopLeftCorner();
    const bottomRight = cellRange.getBottomRightCorner();
    return {
      row: topLeft.row,
      col: topLeft.col,
      rowspan: bottomRight.row - topLeft.row + 1,
      colspan: bottomRight.col - topLeft.col + 1,
    };
  }

  private beforeTableMergeCells(cellRange: CellRange): void {
    // need to update span data before merging cells, because rendering of cells happens before event of 'afterMergeCells'
    // quill-table-renderer needs updated data for that rendering, thus:
    const mergeParent: MergeParent = this.determineMergeParent(cellRange);
    this.updateRowColSpanDataOnMerge(mergeParent);
  }

  private updateRowColSpanDataOnMerge(mergeParent: MergeParent): void {
    if (this.tableProvider) {
      const mergeParentSpanData: ISpanData = this.tableProvider.getSpanData(mergeParent.row, mergeParent.col);
      if (!mergeParentSpanData) {
        return;
      }
      const lineSpanInfo: LineSpanInfo = this.tableProvider.getLineSpanInfoForRow(mergeParent.row);
      const newMultilineCount: number = !lineSpanInfo ? mergeParentSpanData.maxMultilineCount : lineSpanInfo.lineSpan / mergeParent.rowspan;
      if (mergeParentSpanData.textCheckClass) {
        mergeParentSpanData.isTextCheckDirty =
          mergeParentSpanData.isTextCheckDirty ||
          mergeParentSpanData.currentMultilineCount !== newMultilineCount ||
          mergeParentSpanData.maxMultilineCount === newMultilineCount ||
          mergeParentSpanData.colSpan !== mergeParent.colspan;
      }
      mergeParentSpanData.rowSpan = mergeParent.rowspan;
      mergeParentSpanData.colSpan = mergeParent.colspan;
      mergeParentSpanData.currentMultilineCount = newMultilineCount;
      for (let i: number = 0; i < mergeParent.rowspan; i++) {
        for (let j: number = 0; j < mergeParent.colspan; j++) {
          const spanData: ISpanData = this.tableProvider.getSpanData(mergeParent.row + i, mergeParent.col + j);
          spanData.parent = mergeParentSpanData;
        }
      }
      mergeParentSpanData.parent = null; // spanData of merge parent must nor have any parent, it represents all merged cells
    }
  }

  private beforeTableDivideCells(cellRange: CellRange): void {
    // need to update span data before dividing cells, because rendering of cells happens before event of 'afterUnmergeCells'
    // quill-table-renderer needs updated data for that rendering, thus:
    this.updateRowColSpanDataOnDivide(cellRange);
  }

  private updateRowColSpanDataOnDivide(cellRange: CellRange): void {
    if (this.tableProvider) {
      for (let r = cellRange.from.row; r <= cellRange.to.row; r++) {
        for (let c = cellRange.from.col; c <= cellRange.to.col; c++) {
          const spanData: ISpanData = this.tableProvider.getSpanData(r, c);
          if (spanData.textCheckClass) {
            spanData.isTextCheckDirty =
              spanData.isTextCheckDirty || spanData.currentMultilineCount !== spanData.maxMultilineCount || spanData.colSpan !== 1;
          }
          spanData.rowSpan = 1;
          spanData.colSpan = 1;
          spanData.currentMultilineCount = spanData.maxMultilineCount;
          spanData.parent = null;
        }
      }
    }
  }

  private beforeTableCopy(data: any[], coords: any[]): boolean {
    // seems handsontable only provides coords for last selected cell range anyway:
    if (!data || data.length === 0 || !coords || coords.length !== 1) {
      return false;
    }

    // remove data copied from invalid cells:
    for (let i: number = data.length - 1; i >= 0; i--) {
      for (let j: number = data[i].length - 1; j >= 0; j--) {
        const spanData: ISpanData = this.tableProvider.getSpanData(coords[0].startRow + i, coords[0].startCol + j);
        if (!spanData || spanData.parent || !spanData.enabled || spanData.isEndPlate) {
          data[i].splice(j, 1);
        }
      }
      if (data[i].length === 0) {
        data.splice(i, 1);
      }
    }

    // allow copy, in case some data is left:
    return data.length > 0;
  }

  private beforeTablePaste(data: any[], coords: any[]): boolean {
    // seems handsontable only provides coords for last selected cell range anyway:
    if (!data || data.length === 0 || !coords || coords.length !== 1) {
      return false;
    }

    // convert plain text data to html5data and update html5data with appropiate text check annotations for target cell (= target spanData)
    const resultData = [];
    let dataRowIndex: number = 0;
    let dataColIndex: number = 0;
    for (let i: number = coords[0].startRow; i <= coords[0].endRow; i++) {
      const resultDataRow = [];
      let rowUsed: boolean = false;
      let resultDataRowIndex: number = 0;
      resultData.push(resultDataRow);
      for (let j: number = coords[0].startCol; j <= coords[0].endCol; j++) {
        const spanData: ISpanData = this.tableProvider.getSpanData(i, j);
        if (
          spanData &&
          !spanData.parent &&
          spanData.enabled &&
          !spanData.isEndPlate &&
          dataRowIndex < data.length &&
          dataColIndex < data[dataRowIndex].length
        ) {
          let dataStr: string = data[dataRowIndex][dataColIndex];
          rowUsed = true;
          dataColIndex++;
          if (this.markingSystemLabelModeEnabled) {
            if (dataColIndex >= data[dataRowIndex].length) {
              dataColIndex = 0;
              dataRowIndex++;
            }
          }
          if (LabelEditorUtils.isHTML5Data(dataStr)) {
            const resultSpanData: ISpanData = spanData.clone();
            LabelEditorUtils.parseCustomFormatSettingsIntoSpanDataFromHTML5Data(resultSpanData, dataStr);
            resultDataRow[resultDataRowIndex] = LabelEditorUtils.validateAndApplyHTML5Data(dataStr, resultSpanData, this.useStrictTextCheckConformity(), this.localeResourceManager);
          } else {
            dataStr = LabelEditorUtils.replaceTextInHTML5Data(spanData.html5data, dataStr, true);
            resultDataRow[resultDataRowIndex] = LabelEditorUtils.validateAndApplyHTML5Data(dataStr, spanData, this.useStrictTextCheckConformity(), this.localeResourceManager);
          }
        } else {
          resultDataRow[resultDataRowIndex] = '';
        }
        resultDataRowIndex++;
      }
      if (this.markingSystemLabelModeEnabled) {
        // do nothing, as this is handeled above. We do not keep structure here to support copy/paste for marking cards as in flex version: see examples in NGI-399
      } else {
        if (rowUsed) {
          dataColIndex = 0;
          dataRowIndex++;
        }
      }
    }

    // replace original data with modified result data:
    data.length = 0;
    for (let r: number = 0; r < resultData.length; r++) {
      data.push(resultData[r]);
    }
  }

  private updateTableCells(row?: number, col?: number) {
    const cellProperties = {};
    const spanData: ISpanData = this.tableProvider.getSpanData(row, col);
    if (!spanData || !spanData.enabled) {
      cellProperties['readOnly'] = 'true';
      cellProperties['disableVisualSelection'] = false;
    }
    return cellProperties;
  }

  private afterTableDeselect(): void {
    this.selectedCellsDescriptionMaterial = '';
    this.selectedCellsDescriptionPosition = '';
    this.afterTableSelectionEndEmitter.emit();
  }

  private afterTableSelectionEnd(): void {
    this.updateSelectedCellsDescription();
    this.afterTableSelectionEndEmitter.emit();
  }

  private updateSelectedCellsDescription(): void {
    if (!this.isSelectedCellsDescriptionEnabled || this.tableProvider.type === TableProviderType.MARKING_CARD) {
      return;
    }
    const selectedCells: number[][] = LabelEditorUtils.explodeCellRanges(this.tableProvider, this.table.getSelected(), true);
    if (this.tableProvider.type !== TableProviderType.SEPARATE_LABELING) {
      this.updateSelectedCellsDescriptionPosition(selectedCells);
    }
    if (!this.isDirectPrintingTab) {
      this.updateSelectedCellsDescriptionMaterial(selectedCells);
    }
  }

  private updateSelectedCellsDescriptionPosition(selectedCells: number[][]): void {
    let descriptions: string[];
    if (this.isDirectPrintingTab) {
      const directPrintingCellsDescriptions: string[] = [];
      for (const cell of selectedCells) {
        const spanData = this.tableProvider.getSpanData(cell[0], cell[1]);
        if (isConnectionSpanData(spanData) && spanData.description) {
          directPrintingCellsDescriptions.push(spanData.description);
        }
      }
      const uniqueDescriptions: string[] = Array.from(new Set(directPrintingCellsDescriptions));
      descriptions = uniqueDescriptions.sort((a: string, b: string) => {
        return NumberUtils.numericCompare(this.getPositionFromCellDescription(a), this.getPositionFromCellDescription(b));
      });
    } else {
      const selectedColumnIndexes: number[] = this.getSelectedColumnIndexes(selectedCells);
      descriptions = selectedColumnIndexes.map((index: number) => {
        const positionOnRail: string = this.tableProvider.getColumnHeader(index);
        const columnHeaderTooltip: string = this.tableProvider.getColumnHeaderTooltip(index);
        if (positionOnRail && columnHeaderTooltip) {
          const articleOnRailNumber: string = columnHeaderTooltip.split(' ')[0];
          return positionOnRail + LabelEditorTabComponent.SELECTED_CELLS_DESCRIPTION_POSITION_SEPARATOR + articleOnRailNumber;
        }
      });
      descriptions = Array.from(new Set(descriptions.filter((description) => !!description)));
    }
    this.selectedCellsDescriptionPosition = descriptions.join(LabelEditorTabComponent.SELECTED_CELLS_DESCRIPTION_SEPARATOR);
  }

  private getPositionFromCellDescription(description: string): number {
    if (!description) {
      return NaN;
    }
    const position: string = description.split(LabelEditorTabComponent.SELECTED_CELLS_DESCRIPTION_POSITION_SEPARATOR)[0];
    return +position;
  }

  private updateSelectedCellsDescriptionMaterial(selectedCells: number[][]): void {
    const labelingArticles: ArticleVO[] = [];
    for (const cell of selectedCells) {
      const spanData = this.tableProvider.getSpanData(cell[0], cell[1]);
      if (
        isConnectionSpanData(spanData) &&
        spanData.article &&
        !labelingArticles.find((article) => article.identity === spanData.article.identity)
      ) {
        labelingArticles.push(spanData.article);
      }
    }
    if (ArrayUtils.isEmpty(labelingArticles)) {
      this.selectedCellsDescriptionMaterial = '';
    } else if (labelingArticles.length === 1) {
      const color = this.localizedPropertyService.getColorNameFromArticle(labelingArticles[0]);
      const articleDescription: string = LocaleUtils.getValueByLocaleChain(
        labelingArticles[0].displayName,
        this.localeResourceManager.getLocaleChain()
      );
      this.selectedCellsDescriptionMaterial = this.localeResourceManager.getValueByKey('labelEditorPopup.selectedCellsDescription.same_material', {
        '0': labelingArticles[0].articleNo,
        '1': color,
        '2': articleDescription.split(';')[0],
      });
    } else {
      const articleNumbers: string[] = labelingArticles.map((article: ArticleVO) => article.articleNo);
      this.selectedCellsDescriptionMaterial = this.localeResourceManager.getValueByKey(
        'labelEditorPopup.selectedCellsDescription.different_materials',
        {
          '0': articleNumbers.join(LabelEditorTabComponent.SELECTED_CELLS_DESCRIPTION_SEPARATOR),
        }
      );
    }
  }

  private getSelectedColumnIndexes(selectedCells: number[][]): number[] {
    const selectedColumns: number[] = selectedCells.map((selectedCell: number[]) => selectedCell[1]);
    const uniqueSelectedColumns = Array.from(new Set(selectedColumns));
    return uniqueSelectedColumns.sort((a: number, b: number) => NumberUtils.numericCompare(a, b));
  }
}
